import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Checkbox,
    Chip,
    FormHelperText,
    FormLabel,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import {TreeView} from '@mui/x-tree-view/TreeView';
import {ChevronRight, ExpandMore} from '@mui/icons-material';
import {Field} from 'react-final-form';
import Button from '../../base/button/button';
import {
    absorbCategoryIntoSelection,
    buildCategoryTree,
    collectExpandableCategoryNodeIds,
    filterCategoryTreeByQuery,
    getCascadingCategoryTitle,
    isCategoryCoveredByAncestor,
    resolveLeafCategoryIds,
} from './utils';

/**
 * @typedef {{id: number, title?: string, parentId?: number|null, children?: any[]}} CategoryOption
 */

/**
 * Presentational hierarchy multi-select (tree left + chips right).
 * @param {{
 *   categories?: CategoryOption[],
 *   value?: number[],
 *   onChange?: (ids: number[]) => void,
 *   disabled?: boolean,
 *   loading?: boolean,
 *   label?: string,
 *   helperText?: string,
 * }} props
 */
const CategoryHierarchyMultiSelectInner = ({
    categories = [],
    value = [],
    onChange,
    disabled = false,
    loading = false,
    label,
    helperText,
}) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.category-hierarchy-multi-select'});
    const [searchQuery, setSearchQuery] = useState('');
    const [expanded, setExpanded] = useState(/** @type {string[]} */ ([]));

    const selectedIds = useMemo(() => value ?? [], [value]);
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const tree = useMemo(() => buildCategoryTree(categories), [categories]);
    const filteredTree = useMemo(
        () => filterCategoryTreeByQuery(tree, searchQuery),
        [tree, searchQuery],
    );

    /**
     * Expands all matching branches when the search query changes.
     */
    useEffect(() => {
        if (!searchQuery.trim()) {
            return;
        }
        setExpanded(collectExpandableCategoryNodeIds(filteredTree));
    }, [filteredTree, searchQuery]);

    const leafCount = useMemo(
        () => resolveLeafCategoryIds(selectedIds, categories).length,
        [selectedIds, categories],
    );

    /**
     * Toggles a category id in the selection.
     * Selecting a parent absorbs (removes) any selected descendant chips.
     * Nodes already covered by a selected ancestor are ignored.
     * @type {(function(number): void)}
     */
    const toggleCategory = useCallback((categoryId) => {
        if (disabled) {
            return;
        }
        if (selectedIdSet.has(categoryId)) {
            onChange?.(selectedIds.filter((id) => id !== categoryId));
            return;
        }
        if (isCategoryCoveredByAncestor(categoryId, selectedIdSet, categories)) {
            return;
        }
        onChange?.(absorbCategoryIntoSelection(selectedIds, categoryId, categories));
    }, [categories, disabled, onChange, selectedIdSet, selectedIds]);

    /**
     * Removes a single selected category.
     * @type {(function(number): void)}
     */
    const removeCategory = useCallback((categoryId) => {
        if (disabled) {
            return;
        }
        onChange?.(selectedIds.filter((id) => id !== categoryId));
    }, [disabled, onChange, selectedIds]);

    /**
     * Clears the entire selection.
     * @type {(function(): void)}
     */
    const clearAll = useCallback(() => {
        if (disabled) {
            return;
        }
        onChange?.([]);
    }, [disabled, onChange]);

    /**
     * Handles tree expand/collapse node toggles.
     * @type {(function(*, string[]): void)}
     */
    const onNodeToggle = useCallback((_event, nodeIds) => {
        setExpanded(nodeIds);
    }, []);

    /**
     * Renders tree items recursively with checkboxes.
     * @type {(function(CategoryOption[]): React.ReactNode)}
     */
    const renderTreeItems = useCallback((nodes) => {
        return nodes.map((node) => {
            const nodeId = String(node.id);
            const isSelected = selectedIdSet.has(node.id);
            const coveredByAncestor = isCategoryCoveredByAncestor(node.id, selectedIdSet, categories);
            const checked = isSelected || coveredByAncestor;
            const hasChildren = !!node.children?.length;

            return (
                <TreeItem
                    key={nodeId}
                    nodeId={nodeId}
                    label={
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                py: 0.25,
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Checkbox
                                size="small"
                                checked={checked}
                                disabled={disabled || coveredByAncestor}
                                onChange={() => toggleCategory(node.id)}
                                onClick={(event) => event.stopPropagation()}
                                inputProps={{
                                    'aria-label': t('toggleCategoryAria', {title: node.title ?? ''}),
                                }}
                            />
                            <Typography
                                variant="body2"
                                component="span"
                                color={coveredByAncestor ? 'text.secondary' : 'text.primary'}
                            >
                                {node.title}
                            </Typography>
                        </Box>
                    }
                >
                    {hasChildren ? renderTreeItems(node.children) : null}
                </TreeItem>
            );
        });
    }, [categories, disabled, selectedIdSet, t, toggleCategory]);

    return (
        <Box sx={{width: '100%'}}>
            {label && (
                <FormLabel sx={{mb: 1, display: 'block'}}>
                    {label}
                </FormLabel>
            )}
            {(helperText || selectedIds.length > 0) && (
                <FormHelperText sx={{mt: 0, mb: 1.5, mx: 0}}>
                    {helperText}
                    {selectedIds.length > 0 && (
                        <>
                            {helperText ? ' ' : ''}
                            {t('leafCountHint', {count: leafCount})}
                        </>
                    )}
                </FormHelperText>
            )}
            <Grid2 container spacing={2}>
                <Grid2 xs={12} md={6}>
                    <TextField
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder={t('searchPlaceholder')}
                        size="small"
                        fullWidth
                        disabled={disabled || loading}
                        sx={{mb: 1.5}}
                    />
                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            maxHeight: 320,
                            minHeight: 180,
                            overflow: 'auto',
                            px: 1,
                            py: 0.5,
                            bgcolor: 'background.paper',
                        }}
                    >
                        {loading && (
                            <Box sx={{py: 1}}>
                                <Skeleton height={28}/>
                                <Skeleton height={28}/>
                                <Skeleton height={28} width="80%"/>
                            </Box>
                        )}
                        {!loading && !filteredTree.length && (
                            <Typography variant="body2" color="text.secondary" sx={{py: 2, px: 1}}>
                                {searchQuery.trim() ? t('noSearchResults') : t('emptyCategories')}
                            </Typography>
                        )}
                        {!loading && !!filteredTree.length && (
                            <TreeView
                                expanded={expanded}
                                onNodeToggle={onNodeToggle}
                                defaultCollapseIcon={<ExpandMore/>}
                                defaultExpandIcon={<ChevronRight/>}
                                disableSelection
                                sx={{flexGrow: 1}}
                            >
                                {renderTreeItems(filteredTree)}
                            </TreeView>
                        )}
                    </Box>
                </Grid2>
                <Grid2 xs={12} md={6}>
                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            maxHeight: 320 + 40,
                            minHeight: 180,
                            overflow: 'auto',
                            px: 1.5,
                            py: 1.5,
                            bgcolor: 'background.paper',
                            height: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5}}>
                            <Typography variant="subtitle2">
                                {t('selectedLabel', {count: selectedIds.length})}
                            </Typography>
                            {selectedIds.length > 0 && (
                                <Button
                                    type="button"
                                    size="small"
                                    variant="text"
                                    color="secondary"
                                    disabled={disabled}
                                    onClick={clearAll}
                                >
                                    {t('clearAll')}
                                </Button>
                            )}
                        </Box>
                        {!selectedIds.length && (
                            <Typography variant="body2" color="text.secondary">
                                {t('emptySelection')}
                            </Typography>
                        )}
                        {selectedIds.length > 0 && (
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                {selectedIds.map((id) => (
                                    <Chip
                                        key={id}
                                        size="small"
                                        label={getCascadingCategoryTitle(id, categories) || String(id)}
                                        onDelete={disabled ? undefined : () => removeCategory(id)}
                                        disabled={disabled}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Grid2>
            </Grid2>
        </Box>
    );
};

/**
 * Hierarchy category multi-select with optional react-final-form integration.
 * @param {{
 *   categories?: CategoryOption[],
 *   value?: number[],
 *   onChange?: (ids: number[]) => void,
 *   disabled?: boolean,
 *   loading?: boolean,
 *   label?: string,
 *   helperText?: string,
 *   form?: boolean,
 *   name?: string,
 * }} props
 */
const CategoryHierarchyMultiSelect = ({
    form = false,
    name,
    value,
    onChange,
    ...props
}) => {
    if (form && name) {
        return (
            <Field name={name} subscription={{value: true}}>
                {({input}) => (
                    <CategoryHierarchyMultiSelectInner
                        {...props}
                        value={input.value ?? []}
                        onChange={(next) => input.onChange(next)}
                    />
                )}
            </Field>
        );
    }

    return (
        <CategoryHierarchyMultiSelectInner
            {...props}
            value={value}
            onChange={onChange}
        />
    );
};

export default CategoryHierarchyMultiSelect;
export {resolveLeafCategoryIds} from './utils';
